// Plain-language labels for the codes in the ENDURANCE metadata schema
// (Models/Schemas/endurance-metadata-schema-v1.1.json) and the KB's knowledge-item types.
// labels.test.ts fails if the schema or the KnowledgeItemType enum gains a value without a label,
// so a new code never shows up raw on screen.

export interface Label {
  label: string;
  /** One sentence a non-specialist understands. */
  help?: string;
}

type LabelMap = Record<string, Label>;

export const pilots: LabelMap = {
  'MACRO-Europe': { label: 'European pilot (macro)', help: 'Europe-wide scenarios across several countries.' },
  'MESO-Slovenia': { label: 'Slovenian pilot', help: 'Regional pilot in Slovenia, focused on the Savinja river basin.' },
  'MESO-Romania': { label: 'Romanian pilot', help: 'Regional pilot in Romania.' },
  'MICRO-Italy': { label: 'Italian pilot', help: 'Local pilot in Italy.' },
  'MICRO-Greece': { label: 'Greek pilot', help: 'Local pilot in Greece, focused on water supply.' },
  'Cross-Pilot': { label: 'Shared across pilots', help: 'Used by more than one pilot.' },
  'Technical-WP': { label: 'Technical work package', help: 'Produced by the project’s technical teams rather than a pilot.' },
};

export const sectors: LabelMap = {
  Energy: { label: 'Energy' },
  'Energy-Electricity-Transmission': { label: 'Electricity transmission' },
  'Energy-Electricity-Distribution': { label: 'Electricity distribution' },
  'Energy-Gas': { label: 'Gas' },
  'Digital-Infrastructure': { label: 'Digital infrastructure' },
  'Digital-Telecom': { label: 'Telecommunications' },
  'Digital-DNS': { label: 'Internet naming (DNS)' },
  'Digital-Cloud': { label: 'Cloud services' },
  Health: { label: 'Health' },
  'Health-Healthcare-Facilities': { label: 'Healthcare facilities' },
  'Health-Hospitals': { label: 'Hospitals' },
  'Public-Administration': { label: 'Public administration' },
  'Drinking-Water': { label: 'Drinking water' },
  Wastewater: { label: 'Wastewater' },
  'Cross-Sector': { label: 'Several sectors' },
};

export type SensitivityTone = 'success' | 'neutral' | 'warning' | 'error';

export const sensitivity: Record<string, Label & { tone: SensitivityTone; careful: boolean }> = {
  Public: { label: 'Public', help: 'Can be shared openly.', tone: 'success', careful: false },
  Internal: { label: 'Internal', help: 'For people working in the ENDURANCE project.', tone: 'neutral', careful: false },
  Confidential: {
    label: 'Confidential',
    help: 'Only for the people and organisations allowed to see it. Don’t pass it on.',
    tone: 'warning',
    careful: true,
  },
  Restricted: {
    label: 'Restricted',
    help: 'Highly sensitive. Only for named people, on approved systems.',
    tone: 'error',
    careful: true,
  },
};

export const payloadCategories: LabelMap = {
  'Digital-Twin-Ontology': { label: 'Digital twin model', help: 'Describes organisations, assets and how they depend on each other.' },
  'Federated-Learning-Data': { label: 'Machine-learning data', help: 'Data used to train or test shared prediction models.' },
  'Simulation-Output': { label: 'Simulation results', help: 'Output of a model or scenario simulation.' },
  'Sensor-Telemetry': { label: 'Sensor readings', help: 'Measurements collected from sensors or control systems.' },
  'Document-Collection': { label: 'Documents', help: 'Reports, policies or other documents.' },
  'Reference-Data': { label: 'Reference lists', help: 'Shared lists and classifications used by other data.' },
  Unstructured: { label: 'Other files', help: 'Files without a fixed structure.' },
  Custom: { label: 'Custom format', help: 'A format specific to this dataset; see its description.' },
};

export const structureTypes: LabelMap = {
  'DT-OrganizationProfile': { label: 'Organisation profile' },
  'DT-AssetCIInventory': { label: 'Inventory of critical assets' },
  'DT-RiskAssessment': { label: 'Risk assessment' },
  'DT-IncidentHistory': { label: 'Incident history' },
  'DT-CrossSectorDependencies': { label: 'Dependencies between sectors' },
  'DT-Taxonomies': { label: 'Classification lists' },
  'DT-ControlLibrary': { label: 'Library of security controls' },
  'DT-WeightingFramework': { label: 'Weighting framework' },
  'FL-TrainingDataset': { label: 'Training data for federated learning' },
  'FL-ModelWeights': { label: 'Trained model' },
  'FL-ValidationMetrics': { label: 'Model test results' },
  'FL-AggregatedUpdate': { label: 'Combined model update' },
  'FL-ForecastDataset': { label: 'Forecast data' },
  'SIM-ScenarioDefinition': { label: 'Scenario definition' },
  'SIM-CascadeResults': { label: 'Cascading-effects results' },
  'SIM-ResilienceScores': { label: 'Resilience scores' },
  'SIM-StressTestOutput': { label: 'Stress-test results' },
  'TEL-SCADA': { label: 'Control-system (SCADA) readings' },
  'TEL-IoTSensors': { label: 'IoT sensor readings' },
  'TEL-SmartMeters': { label: 'Smart-meter readings' },
  'TEL-EnvironmentalSensors': { label: 'Environmental sensor readings' },
  'DOC-PolicyDocument': { label: 'Policy document' },
  'DOC-IncidentReport': { label: 'Incident report' },
  'DOC-ComplianceEvidence': { label: 'Compliance evidence' },
  'DOC-TrainingMaterial': { label: 'Training material' },
  'REF-SectorTaxonomy': { label: 'Sector classification' },
  'REF-ThreatCatalogue': { label: 'Threat catalogue' },
  'REF-ControlCatalogue': { label: 'Control catalogue' },
  'REF-RegulatoryMapping': { label: 'Regulation mapping' },
  Unstructured: { label: 'No fixed structure' },
  Custom: { label: 'Custom structure' },
};

export const dataNature: LabelMap = {
  'Real-Operational': { label: 'Real operational data', help: 'Collected from real systems in operation.' },
  'Synthetic-Validated': { label: 'Simulated, checked against reality', help: 'Generated data that experts have compared with real data.' },
  'Synthetic-Unvalidated': { label: 'Simulated, not yet checked', help: 'Generated data that hasn’t been compared with real data.' },
  Anonymized: { label: 'Anonymised', help: 'Real data with personal details removed.' },
  Aggregated: { label: 'Summarised', help: 'Totals or averages rather than individual records.' },
  Mixed: { label: 'Mixed', help: 'A combination of the above.' },
};

export const qualityLevels: LabelMap = {
  Production: { label: 'Ready for use', help: 'Checked and used in real work.' },
  'Pre-production': { label: 'Nearly ready', help: 'Being prepared for real use; may still change.' },
  Experimental: { label: 'Experimental', help: 'For trials and research only.' },
  Unvalidated: { label: 'Not checked', help: 'Nobody has checked this data yet.' },
};

export const validationStatus: LabelMap = {
  Verified: { label: 'Verified' },
  Pending: { label: 'Check in progress' },
  'Known-Issues': { label: 'Has known problems' },
};

export const updateFrequency: LabelMap = {
  'Real-time': { label: 'Continuously' },
  Hourly: { label: 'Every hour' },
  Daily: { label: 'Every day' },
  Weekly: { label: 'Every week' },
  Monthly: { label: 'Every month' },
  Quarterly: { label: 'Every three months' },
  Annually: { label: 'Every year' },
  Static: { label: 'Not updated' },
  Irregular: { label: 'From time to time' },
};

export const accessTypes: LabelMap = {
  Public: { label: 'Open to everyone' },
  'Restricted-Internal': { label: 'Project members only' },
  'Restricted-Partner': { label: 'Named partners only' },
  Confidential: { label: 'Confidential' },
};

// ---- Knowledge-item types -------------------------------------------------------------------

export type KindGroup = 'datasets' | 'documents' | 'models' | 'training' | 'resilience';

export const kindGroups: Record<KindGroup, Label> = {
  datasets: { label: 'Datasets', help: 'Data files with a description of where they come from.' },
  documents: { label: 'Documents and media', help: 'PDFs, images, videos and other files.' },
  models: { label: 'Models and results', help: 'Machine-learning models, training runs and test results.' },
  training: { label: 'Training and exercises', help: 'Quizzes, checklists, exercises and other learning material.' },
  resilience: { label: 'Assets, risks and incidents', help: 'Descriptions of critical assets, risks and past incidents.' },
};

/** Every KnowledgeItemType value (Models/KnowledgeItem.cs), with its label and group. */
export const itemTypes: Record<string, Label & { group: KindGroup }> = {
  Dataset: { label: 'Dataset', group: 'datasets' },
  Image: { label: 'Image', group: 'documents' },
  Video: { label: 'Video', group: 'documents' },
  PDF: { label: 'PDF document', group: 'documents' },
  Default: { label: 'File', group: 'documents' },
  Unity: { label: '3D / virtual-reality scene', group: 'training' },
  Chatbot: { label: 'Chatbot', group: 'training' },
  Questionnaire: { label: 'Questionnaire', group: 'training' },
  Checklist: { label: 'Checklist', group: 'training' },
  Workflow: { label: 'Step-by-step procedure', group: 'training' },
  MQTT_Template: { label: 'Device message template', group: 'training' },
  Answers: { label: 'Answers', group: 'training' },
  Quiz: { label: 'Quiz', group: 'training' },
  Exercise: { label: 'Exercise', group: 'training' },
  ExerciseRound: { label: 'Exercise round', group: 'training' },
  Inject: { label: 'Exercise event', group: 'training' },
  ExerciseResult: { label: 'Exercise result', group: 'training' },
  CriticalEntity: { label: 'Critical organisation', group: 'resilience' },
  Asset: { label: 'Asset', group: 'resilience' },
  Risk: { label: 'Risk', group: 'resilience' },
  Incident: { label: 'Incident', group: 'resilience' },
  ServiceDependency: { label: 'Service dependency', group: 'resilience' },
  CIFacility: { label: 'Critical facility', group: 'resilience' },
  FederatedTrainingRun: { label: 'Model training run', group: 'models' },
  MLModelVersion: { label: 'Machine-learning model', group: 'models' },
  MLEvaluation: { label: 'Model test result', group: 'models' },
};

// The API writes types as enum names ("Dataset") in some responses and camel case ("dataset",
// "mqtT_Template") in others; compare on a normalised key.
const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
const typeIndex = new Map(Object.keys(itemTypes).map((k) => [normalise(k), k]));

/** Canonical enum name for an API type string, or undefined if unknown. */
export function canonicalType(apiType: string | null | undefined): string | undefined {
  return apiType ? typeIndex.get(normalise(apiType)) : undefined;
}

export function typeLabel(apiType: string | null | undefined): string {
  const key = canonicalType(apiType);
  return key ? itemTypes[key].label : (apiType ?? '');
}

export function typesInGroup(group: KindGroup): string[] {
  return Object.entries(itemTypes)
    .filter(([, v]) => v.group === group)
    .map(([k]) => k);
}

/** Label for a code from one of the maps above; falls back to the code itself. */
export function labelOf(map: LabelMap, code: string | null | undefined): string {
  if (!code) return '';
  return map[code]?.label ?? code;
}
